import {useEffect, useRef, useState} from 'react';

import {Integer} from '../types/Integer';
import {getStepsFromPixels} from '../utils/getStepsFromPixels';

type UseAvailableWidthReturn<T extends HTMLElement> = {
  availableWidthRef: React.RefObject<T | null>;
  availableWidth: Integer;
};

/**
 * Hook that calculates available width for an element based on viewport width and element's position
 * @returns Object containing the ref to attach to the element and the available width in steps
 * @example
 * const {availableWidth, ref} = useAvailableWidth<HTMLDivElement>();
 * return <Box ref={ref} width={availableWidth} />;
 */
export function useAvailableWidth<T extends HTMLElement>(): UseAvailableWidthReturn<T> {
  const availableWidthRef = useRef<T | null>(null);
  const [availableWidth, setAvailableWidth] = useState<Integer>(0);

  useEffect(() => {
    const calculateWidth = () => {
      if (!availableWidthRef.current) {
        return;
      }

      const rect = availableWidthRef.current.getBoundingClientRect();
      const viewportWidth = window.innerWidth;
      const remainingWidth = viewportWidth - rect.left;

      if (remainingWidth > 0) {
        const steps = getStepsFromPixels(remainingWidth);
        if (steps) {
          setAvailableWidth(steps);
        }
      }
    };

    calculateWidth();

    const resizeObserver = new ResizeObserver(calculateWidth);
    resizeObserver.observe(window.document.body);

    return () => {
      resizeObserver.disconnect();
    };
  }, []);

  return {availableWidthRef, availableWidth};
}
